<template lang="pug">
  y-dialog(v-model="showDialog" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="600" persistent scrollable content-class="round-3xl")
    v-card(min-height="90vh")
      v-card-title.pa-4
        v-row(no-gutters align="center")
          v-col(cols="10" offset="1")
            div.text-xl.font-bold.neutral700--text.text-center {{ $t('edit_item_detail', { item:$t('customer').toLowerCase() }) }}
          v-col(cols="1" align="right")
            v-btn.ml-n3(text icon @click="closeDialog")
              v-icon(color="neutral700" :size="24") mdi-close
      v-divider
      v-card-text(:class="$vuetify.breakpoint.xsOnly ? 'pa-4' : 'pa-8'")
        v-form(ref="form")
          div.text-md.font-semibold.neutral700--text {{ $t('make_change_on_details') }}
          div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('name') }}
            v-text-field.text-sm.mt-2.round-xl(v-model="customer.name" :placeholder="$t('name')" outlined :rules="rules.not_empty" hide-details="auto")
            div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('phone') }}
            phone-input.mt-2(v-model="customer.phone" required)
            div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('email') }}
            v-text-field.text-sm.mt-2.round-xl(v-model="customer.email" :placeholder="$t('email_optional')" outlined hide-details="auto" :rules="customer.email ? rules.email : []")
            .address
              div.text-sm.font-semibold.neutral500--text.mt-4 {{$t('address_1.label')}}
              v-text-field.text-sm.mt-2.round-xl(v-model="address.address_1" :placeholder="$t('address_1.placeholder')" outlined :rules="emptyAddress ? [] : rules.not_empty" hide-details="auto")
              div.text-sm.font-semibold.neutral500--text.mt-4 {{$t('address_2.label')}}
              v-text-field.text-sm.mt-2.round-xl(v-model="address.address_2" :placeholder="$t('address_2.placeholder')" outlined hide-details="auto")
              div.text-sm.font-semibold.neutral500--text.mt-4 {{$t('postcode.label')}}
              v-text-field.text-sm.mt-2.round-xl(v-model="address.postcode" :placeholder="$t('postcode.placeholder')" outlined :rules="emptyAddress ? [] : rules.not_empty" hide-details="auto" @keyup="fetchPostcodes(address.postcode)")
              div.text-sm.font-semibold.neutral500--text.mt-4 {{$t('city.label')}}
              v-text-field.text-sm.mt-2.round-xl(v-model="address.city" :placeholder="$t('city.placeholder')" outlined :rules="emptyAddress ? [] : rules.not_empty" hide-details="auto" :loading="isFetchingAddress")
              div.text-sm.font-semibold.neutral500--text.mt-4 {{$t('state.label')}}
              v-text-field.text-sm.mt-2.round-xl(v-model="address.state" :placeholder="$t('state.placeholder')" outlined :rules="emptyAddress ? [] : rules.not_empty" hide-details="auto" :loading="isFetchingAddress")
              div.text-sm.font-semibold.neutral500--text.mt-4 {{$t('country.label')}}
              v-text-field.text-sm.mt-2.round-xl(v-model="address.country" :placeholder="$t('country.placeholder')" outlined :rules="emptyAddress ? [] : rules.not_empty" hide-details="auto" :loading="isFetchingAddress")
      v-divider
      v-card-actions.py-4(:class="$vuetify.breakpoint.xsOnly ? 'px-4' : 'px-8'")
        v-btn.button-md.neutral700--text.px-0(@click="closeDialog" text) {{ $t('cancel') }}
        v-spacer
        v-btn.button-md(:loading="isLoading" color="neutral700" depressed :dark="hasChanges" :disabled="!hasChanges" @click="saveData") {{ $t('save') }}
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PhoneInput from '@/components/review/custom/PhoneInput.vue'

export default {
  name: 'EditCustomerDetailsDialog',
  components: {
    PhoneInput,
  },
  props: {
    open: {
      type: Boolean,
    },
    selectedCustomer: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isLoading: false,
      customer: {},
      originalData: null,
      isFetchingAddress: false,
      address: {},
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required'],
        email: [
        v => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || 'E-mail must be valid',
        ]
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (val) {
        this.$emit('update:open', val)
      }
    },
    hasChanges () {
      let original = { 
        name: this.originalData.name,
        phone: this.originalData.phone,
        email: this.originalData.email,
        address: this.originalData.address
      }
      return JSON.stringify(this.payloadData) !== JSON.stringify(original)
    },
    emptyAddress() {
      if (Object.keys(this.address).length === 0) {
        return true
      } else if (Object.values(this.address).every(val => !val)) {
        return true
      }
      return false
    },
    payloadData() {
      let payload = { ...this.customer }
      if (!this.checkEmptyAddress(this.address)) {
        payload.address = this.address
      }
      return payload
    }
  },
  mounted () {
    this.setData()
  },
  methods: {
    ...mapActions(['showMessage']),
    setData() {
      const data = {
        name: this.selectedCustomer.name,
        phone: this.selectedCustomer.phone,
        email: this.selectedCustomer.email,
        address: null
      }
      this.customer = { ...data }
      this.originalData = { ...data }
      if (!this.checkEmptyAddress(this.selectedCustomer.address)) {
        this.address = { ...this.selectedCustomer.address }
        this.originalData.address = { ...this.selectedCustomer.address }
      }
    },
    fetchPostcodes(val) {
      if (!val) {
        return
      }
      if (val.length !== 5) {
        return
      }
      this.isFetchingAddress = true
      this.$axios.get(`/shipping/postcodes?postcode=${this.address.postcode}`).then((response) => {
        const addresses = response.data
        if (addresses.length > 0 && addresses[0].postcode) {
          this.$set(this.address, 'city', addresses[0].city)
          this.$set(this.address, 'state', addresses[0].state)
          this.$set(this.address, 'country', addresses[0].country)
        }
      })
      .catch(() => {
      })
      .finally(() => {
        this.isFetchingAddress = false
      })
    },
    saveData() {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isLoading = true
      this.$axios.patch(`/connect/${this.company.slug}/customers/${this.selectedCustomer.id}`, this.payloadData)
        .then(({ data }) => {
          this.showMessage(['success', this.$t('success_message.customer_edited')])
          this.closeResetDialog(data)
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data) {
            message = err.response.data[0]
          }
          this.showMessage(['error', message])
          this.isLoading = false
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    closeResetDialog(data) {
      this.showDialog = false
      this.$emit('reset', data)
    },
    closeDialog() {
      this.showDialog = false
    },
    checkEmptyAddress(address) {
      if (!address) {
        return true
      }
      if (Object.keys(address).length === 0) {
        return true
      } else if (Object.values(address).every(val => !val)) {
        return true
      }
      return false
    },
    getAddressInString (addr) {
      let address = ''
      if (addr.address_1) {
        address += addr.address_1
      } if (addr.address_2) {
        address += `, ${addr.address_2}`
      } if (addr.postcode) {
        address += `, ${addr.postcode}`
      } if (addr.city) {
        address += `, ${addr.city}`
      } if (addr.state) {
        address += `, ${addr.state}`
      } if (addr.country) {
        address += `, ${addr.country}`
      }
      return address
    },
    copyText (text) {
      let container = this.$refs.copy
      this.$copyText(text, container)
        .then(() => {
          this.showMessage(['success', this.$t('success_message.content_copied_to_clipboard')])
        })
    }
  },
  destroyed () {
    this.$store.commit('clinic/resetTempAppointment')
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-input--has-state {
    .v-label {
      color: var(--v-danger) !important;
    }
    .v-input__slot {
      border: 1px solid var(--v-danger) !important;
    }
    .error--text {
      color: var(--v-danger) !important;
    }
  }
  .v-input--has-state.v-input--is-focused {
    .v-label {
      color: var(--v-danger) !important;
    }
    .v-input__slot {
      border: 1.5px solid var(--v-danger) !important;
    }
  }
  .theme--light.v-input input,
  .theme--light.v-input textarea {
    color: #333;
    caret-color: #333;
    font-weight: 500 !important;
    font-size: 16px !important;
    line-height: 24px !important;
  }
  .theme--light.v-input.error--text input,
  .theme--light.v-input.error--text textarea {
    color: var(--v-danger) !important;
    caret-color: var(--v-danger);
  }
  .v-input--is-focused {
    .v-label {
      color: var(--v-neutral500) !important;
    }
    .v-input__slot {
      border: 2px solid var(--v-neutral400) !important;
    }
    fieldset {
      border-color: transparent !important;
    }
  }
  .v-text-field--enclosed .v-input__append-inner {
    margin: auto 0 !important;
  }
  .mdi::before {
    font-size: 20px;
    color: #333;
  }
  .v-text-field .v-input__control .v-input__slot {
    min-height: 56px !important;
    display: flex !important;
  }
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  }
  .error--text {
    color: var(--v-danger) !important;
    fieldset {
      border-color: transparent;
    }
  }
  .phoneLeft {
    border-radius: 10px 0 0 10px;
    padding-right: 0px;
    color: transparent !important;
    fieldset {
      border-right: 0 !important;
    }
    div {
      padding-right: 0px !important;
    }
  }
  .phoneRight {
    border-radius: 0 10px 10px 0;
    border-left: 0px;
    color: transparent !important;
    fieldset {
      border-left: 0 !important;
    }    
  }
}

.checklist-error {
  border: 1px solid var(--v-danger);
}

.checklist-normal {
  border: none;
}

.disabled {
  border-radius: 10px;
  background-color: var(--v-neutral300) !important;
}
</style>

<i18n>
{
  "en": {
    "address_1": {
      "label": "Address Line 1",
      "placeholder": "e.g. K-3-01, Conezion, Persiaran IRC3"
    },
    "address_2": {
      "label": "Address Line 2",
      "placeholder": "e.g. IOI Resort City"
    },
    "postcode": {
      "label": "Postcode",
      "placeholder": "e.g. 62502"
    },
    "city": {
      "label": "City",
      "placeholder": "e.g. Putrajaya"
    },
    "state": {
      "label": "State",
      "placeholder": "e.g. Wilayah Persekutuan Putrajaya"
    },
    "country": {
      "label": "Country",
      "placeholder": "e.g. Malaysia"
    }
  },
  "id": {
   "address_1": {
      "label": "Nama Jalan, Gedung, No Rumah, RT/RW, Kelurahan",
      "placeholder": "e.g. Jalan Prof. Joko Sutono Sh No.999, RT.14/RW.6, Petogogan, "
    },
    "address_2": {
      "label": "Kecamatan",
      "placeholder": "Kec. Kby. Baru"
    },
    "postcode": {
      "label": "Kode Pos",
      "placeholder": "12160"
    },
    "city": {
      "label": "Kota",
      "placeholder": "Kota Jakarta Selatan"
    },
    "state": {
      "label": "Provinsi",
      "placeholder": "DKI Jakarta"
    },
    "country": {
      "label": "Country",
      "placeholder": "e.g. Indonesia"
    }
  }
}
</i18n>